/**
 * EMZI NEXUS V6.4 - Live Studio V2
 * Step 3: Target Audience + Panggilan + Nada dengan AI Suggestions
 */

import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { EMZI_SPACING, EMZI_RADIUS } from '../../../styles/emziCI';
import * as api from '../../../api/liveStudioV2';

// Age range options
const AGE_RANGES = [
  { id: '18-24', label: '18-24', desc: 'Gen Z' },
  { id: '25-34', label: '25-34', desc: 'Young Adults' },
  { id: '35-44', label: '35-44', desc: 'Adults' },
  { id: '45-54', label: '45-54', desc: 'Middle Age' },
  { id: '55+', label: '55+', desc: 'Senior' }
];

// Gender options
const GENDERS = [
  { id: 'all', label: 'Semua', icon: '👥' },
  { id: 'female', label: 'Wanita', icon: '👩' },
  { id: 'male', label: 'Lelaki', icon: '👨' }
];

// Panggilan options
const PANGGILAN_OPTIONS = [
  { id: 'korang', text: 'Korang', desc: 'Casual untuk audiens muda' },
  { id: 'anda_semua', text: 'Anda Semua', desc: 'Formal dan universal' },
  { id: 'guys', text: 'Guys', desc: 'Trendy dan urban' },
  { id: 'kawan2', text: 'Kawan-kawan', desc: 'Mesra dan friendly' },
  { id: 'puan_tuan', text: 'Puan/Tuan', desc: 'Sangat formal' },
  { id: 'custom', text: '✏️ Custom', desc: 'Tulis sendiri' }
];

// Nada options
const NADA_OPTIONS = [
  { id: 'semangat', text: 'Bersemangat', icon: '🔥', desc: 'High energy' },
  { id: 'mesra', text: 'Mesra', icon: '💖', desc: 'Warm dan friendly' },
  { id: 'santai', text: 'Santai', icon: '😎', desc: 'Relaxed dan casual' },
  { id: 'yakin', text: 'Yakin', icon: '💪', desc: 'Confident dan authoritative' },
  { id: 'kelakar', text: 'Kelakar', icon: '😂', desc: 'Humorous' },
  { id: 'informatif', text: 'Informatif', icon: '📚', desc: 'Educational' },
  { id: 'custom', text: '✏️ Custom', icon: '✏️', desc: 'Tulis sendiri' }
];

const Step3Audience = ({ sessionId, token, sessionData, onComplete, onBack, setError, setLoading }) => {
  const { language } = useLanguage();
  
  // Audience state
  const [selectedAges, setSelectedAges] = useState(sessionData?.audience?.ageRanges || []);
  const [selectedGender, setSelectedGender] = useState(sessionData?.audience?.gender || 'all');
  
  // Panggilan & Nada state
  const [panggilan, setPanggilan] = useState(sessionData?.panggilan || null);
  const [panggilanCustom, setPanggilanCustom] = useState(sessionData?.panggilanCustom || '');
  const [nada, setNada] = useState(sessionData?.nada || null);
  const [nadaCustom, setNadaCustom] = useState(sessionData?.nadaCustom || '');
  
  // AI Suggestion state
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [usedAISuggestion, setUsedAISuggestion] = useState(false);

  const getText = (key) => {
    const translations = {
      en: {
        title: 'Target Audience',
        subtitle: 'Define who you\'re talking to',
        ageLabel: 'Age Range (can select multiple)',
        genderLabel: 'Gender',
        panggilanLabel: 'How to address audience?',
        panggilanHint: 'Form of address for your viewers',
        nadaLabel: 'Tone of voice',
        nadaHint: 'How should you sound?',
        aiSuggestBtn: '✨ Get AI Suggestion',
        aiSuggestLoading: 'AI analyzing...',
        aiSuggestionTitle: '✨ AI Suggestion',
        useThisBtn: 'Use This',
        continueBtn: 'Continue to Knowledge →',
        customPlaceholder: 'Enter custom...',
        errors: {
          age: 'Please select at least one age range',
          panggilan: 'Please select panggilan',
          nada: 'Please select nada'
        }
      },
      ms: {
        title: 'Sasaran Audiens',
        subtitle: 'Tentukan siapa yang anda sasarkan',
        ageLabel: 'Julat Umur (boleh pilih lebih dari satu)',
        genderLabel: 'Jantina',
        panggilanLabel: 'Panggilan untuk audiens?',
        panggilanHint: 'Cara memanggil penonton anda',
        nadaLabel: 'Nada suara',
        nadaHint: 'Bagaimana anda harus kedengaran?',
        aiSuggestBtn: '✨ Dapatkan Cadangan AI',
        aiSuggestLoading: 'AI menganalisis...',
        aiSuggestionTitle: '✨ Cadangan AI',
        useThisBtn: 'Guna Ini',
        continueBtn: 'Teruskan ke Knowledge →',
        customPlaceholder: 'Masukkan custom...',
        errors: {
          age: 'Sila pilih sekurang-kurangnya satu julat umur',
          panggilan: 'Sila pilih panggilan',
          nada: 'Sila pilih nada'
        }
      },
      bm: {
        title: 'Sasaran Audiens',
        subtitle: 'Tentukan siapa yang anda sasarkan',
        ageLabel: 'Julat Umur (boleh pilih lebih dari satu)',
        genderLabel: 'Jantina',
        panggilanLabel: 'Panggilan untuk audiens?',
        panggilanHint: 'Cara memanggil penonton anda',
        nadaLabel: 'Nada suara',
        nadaHint: 'Bagaimana anda harus kedengaran?',
        aiSuggestBtn: '✨ Dapatkan Cadangan AI',
        aiSuggestLoading: 'AI menganalisis...',
        aiSuggestionTitle: '✨ Cadangan AI',
        useThisBtn: 'Guna Ini',
        continueBtn: 'Teruskan ke Knowledge →',
        customPlaceholder: 'Masukkan custom...',
        errors: {
          age: 'Sila pilih sekurang-kurangnya satu julat umur',
          panggilan: 'Sila pilih panggilan',
          nada: 'Sila pilih nada'
        }
      },
      urban: {
        title: 'Target Audience',
        subtitle: 'Define siapa yang korang nak cakap dengan',
        ageLabel: 'Age Range (boleh select multiple)',
        genderLabel: 'Gender',
        panggilanLabel: 'Macam mana nak address audiens?',
        panggilanHint: 'Cara panggil viewers korang',
        nadaLabel: 'Tone of voice',
        nadaHint: 'Macam mana korang nak sound?',
        aiSuggestBtn: '✨ Get AI Suggestion',
        aiSuggestLoading: 'AI analyzing...',
        aiSuggestionTitle: '✨ AI Suggestion',
        useThisBtn: 'Use This',
        continueBtn: 'Continue ke Knowledge →',
        customPlaceholder: 'Enter custom...',
        errors: {
          age: 'Please select at least one age range',
          panggilan: 'Please select panggilan',
          nada: 'Please select nada'
        }
      }
    };
    return translations[language]?.[key] || translations.en[key];
  };

  // Toggle age selection
  const toggleAge = (ageId) => {
    if (selectedAges.includes(ageId)) {
      setSelectedAges(selectedAges.filter(a => a !== ageId));
    } else {
      setSelectedAges([...selectedAges, ageId]);
    }
  };

  // Get AI Suggestion
  const getAISuggestion = async () => {
    if (selectedAges.length === 0) {
      setError(getText('errors').age);
      return;
    }
    
    const host = sessionData?.hosts?.[0];
    if (!host) {
      setError('Host profile required for AI suggestion');
      return;
    }
    
    setLoadingAI(true);
    setError(null);
    
    try {
      const result = await api.getAISuggestion(
        token,
        {
          ageRange: host.ageRange,
          gender: host.gender,
          personality: host.personality
        },
        {
          ageRanges: selectedAges,
          gender: selectedGender
        }
      );
      
      setAiSuggestions(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAI(false);
    }
  };

  // Apply AI suggestion
  const applyAISuggestion = (type, suggestion) => {
    if (type === 'panggilan') {
      setPanggilan(suggestion.id);
      setUsedAISuggestion(true);
    } else if (type === 'nada') {
      setNada(suggestion.id);
      setUsedAISuggestion(true);
    }
  };

  // Validate and continue
  const handleContinue = () => {
    if (selectedAges.length === 0) {
      setError(getText('errors').age);
      return;
    }
    if (!panggilan) {
      setError(getText('errors').panggilan);
      return;
    }
    if (!nada) {
      setError(getText('errors').nada);
      return;
    }
    
    // Custom validation
    if (panggilan === 'custom' && !panggilanCustom.trim()) {
      setError('Please enter custom panggilan');
      return;
    }
    if (nada === 'custom' && !nadaCustom.trim()) {
      setError('Please enter custom nada');
      return;
    }
    
    setError(null);
    onComplete({
      audience: {
        ageRanges: selectedAges,
        gender: selectedGender
      },
      panggilan,
      panggilanCustom: panggilan === 'custom' ? panggilanCustom : null,
      nada,
      nadaCustom: nada === 'custom' ? nadaCustom : null,
      usedAISuggestion
    });
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: EMZI_SPACING.xl, textAlign: 'center' }}>
        <h1 style={{ fontSize: '22px', fontWeight: '700', marginBottom: EMZI_SPACING.sm, color: '#fff' }}>
          👥 {getText('title')}
        </h1>
        <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)' }}>{getText('subtitle')}</p>
      </div>

      {/* Age Range Selection */}
      <Card style={{ marginBottom: EMZI_SPACING.lg, borderRadius: EMZI_RADIUS.md }}>
        <CardHeader>
          <CardTitle style={{ fontSize: '16px', fontWeight: '600' }}>
            📊 {getText('ageLabel')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {AGE_RANGES.map(age => (
              <button
                key={age.id}
                onClick={() => toggleAge(age.id)}
                style={{
                  padding: '12px 20px',
                  borderRadius: '8px',
                  border: selectedAges.includes(age.id) ? '2px solid #0e67a2' : '1px solid #ddd',
                  background: selectedAges.includes(age.id) ? '#E8F4FF' : '#fff',
                  color: selectedAges.includes(age.id) ? '#0e67a2' : '#666',
                  fontWeight: selectedAges.includes(age.id) ? '600' : '400',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ fontSize: '14px', fontWeight: '600' }}>{age.label}</div>
                <div style={{ fontSize: '11px', opacity: 0.7 }}>{age.desc}</div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Gender Selection */}
      <Card style={{ marginBottom: EMZI_SPACING.lg, borderRadius: EMZI_RADIUS.md }}>
        <CardHeader>
          <CardTitle style={{ fontSize: '16px', fontWeight: '600' }}>
            {getText('genderLabel')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ display: 'flex', gap: '12px' }}>
            {GENDERS.map(g => (
              <button
                key={g.id}
                onClick={() => setSelectedGender(g.id)}
                style={{
                  flex: 1,
                  padding: '14px',
                  borderRadius: '8px',
                  border: selectedGender === g.id ? '2px solid #0e67a2' : '1px solid #ddd',
                  background: selectedGender === g.id ? '#E8F4FF' : '#fff',
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
              >
                <div style={{ fontSize: '24px', marginBottom: '4px' }}>{g.icon}</div>
                <div style={{ fontSize: '14px', color: selectedGender === g.id ? '#0e67a2' : '#666', fontWeight: selectedGender === g.id ? '600' : '400' }}>
                  {g.label}
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* AI Suggestion Button */}
      <div style={{ textAlign: 'center', marginBottom: EMZI_SPACING.lg }}>
        <button
          onClick={getAISuggestion}
          disabled={loadingAI || selectedAges.length === 0}
          style={{
            padding: '14px 28px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            borderRadius: '10px',
            color: '#fff',
            fontSize: '15px',
            fontWeight: '600',
            cursor: loadingAI || selectedAges.length === 0 ? 'not-allowed' : 'pointer',
            opacity: loadingAI || selectedAges.length === 0 ? 0.6 : 1
          }}
        >
          {loadingAI ? getText('aiSuggestLoading') : getText('aiSuggestBtn')}
        </button>
      </div>

      {/* AI Suggestions Display */}
      {aiSuggestions && (
        <Card style={{ marginBottom: EMZI_SPACING.lg, borderRadius: EMZI_RADIUS.md, background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)', border: '2px solid #667eea' }}>
          <CardHeader>
            <CardTitle style={{ fontSize: '16px', fontWeight: '600', color: '#667eea' }}>
              {getText('aiSuggestionTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
              {aiSuggestions.reasoning}
            </p>
            
            {/* Panggilan Suggestions */}
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Panggilan:</h4>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {aiSuggestions.panggilan_suggestions?.map((s, idx) => (
                  <button
                    key={`item-${idx}`}
                    onClick={() => applyAISuggestion('panggilan', s)}
                    style={{
                      padding: '10px 16px',
                      background: '#fff',
                      border: '1px solid #667eea',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ fontWeight: '600', color: '#667eea' }}>{s.text}</div>
                    <div style={{ fontSize: '11px', color: '#999' }}>{s.reason}</div>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Nada Suggestions */}
            <div>
              <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Nada:</h4>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {aiSuggestions.nada_suggestions?.map((s, idx) => (
                  <button
                    key={`item-${idx}`}
                    onClick={() => applyAISuggestion('nada', s)}
                    style={{
                      padding: '10px 16px',
                      background: '#fff',
                      border: '1px solid #667eea',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ fontWeight: '600', color: '#667eea' }}>{s.text}</div>
                    <div style={{ fontSize: '11px', color: '#999' }}>{s.reason}</div>
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Panggilan Selection */}
      <Card style={{ marginBottom: EMZI_SPACING.lg, borderRadius: EMZI_RADIUS.md }}>
        <CardHeader>
          <CardTitle style={{ fontSize: '16px', fontWeight: '600' }}>
            🗣️ {getText('panggilanLabel')}
          </CardTitle>
          <p style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>{getText('panggilanHint')}</p>
        </CardHeader>
        <CardContent>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
            {PANGGILAN_OPTIONS.map(p => (
              <button
                key={p.id}
                onClick={() => setPanggilan(p.id)}
                style={{
                  padding: '12px 8px',
                  borderRadius: '8px',
                  border: panggilan === p.id ? '2px solid #F89B29' : '1px solid #ddd',
                  background: panggilan === p.id ? '#FFF5E6' : '#fff',
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
              >
                <div style={{ fontSize: '14px', fontWeight: '600', color: panggilan === p.id ? '#F89B29' : '#333' }}>
                  {p.text}
                </div>
                <div style={{ fontSize: '10px', color: '#999' }}>{p.desc}</div>
              </button>
            ))}
          </div>
          
          {/* Custom Input */}
          {panggilan === 'custom' && (
            <input
              type="text"
              value={panggilanCustom}
              onChange={(e) => setPanggilanCustom(e.target.value)}
              placeholder={getText('customPlaceholder')}
              style={{
                width: '100%',
                padding: '12px',
                marginTop: '12px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* Nada Selection */}
      <Card style={{ marginBottom: EMZI_SPACING.lg, borderRadius: EMZI_RADIUS.md }}>
        <CardHeader>
          <CardTitle style={{ fontSize: '16px', fontWeight: '600' }}>
            🎭 {getText('nadaLabel')}
          </CardTitle>
          <p style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>{getText('nadaHint')}</p>
        </CardHeader>
        <CardContent>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
            {NADA_OPTIONS.map(n => (
              <button
                key={n.id}
                onClick={() => setNada(n.id)}
                style={{
                  padding: '12px',
                  borderRadius: '8px',
                  border: nada === n.id ? '2px solid #0e67a2' : '1px solid #ddd',
                  background: nada === n.id ? '#E8F4FF' : '#fff',
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
              >
                <span style={{ fontSize: '24px' }}>{n.icon}</span>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: nada === n.id ? '#0e67a2' : '#333' }}>
                    {n.text}
                  </div>
                  <div style={{ fontSize: '11px', color: '#999' }}>{n.desc}</div>
                </div>
              </button>
            ))}
          </div>
          
          {/* Custom Input */}
          {nada === 'custom' && (
            <input
              type="text"
              value={nadaCustom}
              onChange={(e) => setNadaCustom(e.target.value)}
              placeholder={getText('customPlaceholder')}
              style={{
                width: '100%',
                padding: '12px',
                marginTop: '12px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
        <button
          onClick={onBack}
          style={{
            flex: 1,
            padding: '14px',
            background: '#fff',
            border: '2px solid #0e67a2',
            borderRadius: '10px',
            color: '#0e67a2',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          ← Back
        </button>
        <button
          onClick={handleContinue}
          style={{
            flex: 2,
            padding: '14px',
            background: '#F7941E',
            border: 'none',
            borderRadius: '10px',
            color: '#fff',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          {getText('continueBtn')}
        </button>
      </div>
    </div>
  );
};

export default Step3Audience;
